'use client'

import { AppText } from '@otomoro/components/shared/AppText'
import { PortableText, PortableTextComponents } from 'next-sanity'
import { AppLink } from '@otomoro/components/shared/AppLink'
import { Button } from '@otomoro/components/shared/Button'
import React, { useEffect, useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { contactValidationSchema, Inputs } from './schema'
import { sendEmail } from './action'
import { contactClasses } from './ContactForm.css'
import clsx from 'clsx'
import { PortableTextObject } from 'sanity'
import { SelectArrow } from '@otomoro/components/icons/SelectArrow'

interface ContactFormProps {
	contactSubjects: string[] | undefined
	email: string | undefined
	phone: string | undefined
	address: PortableTextObject | undefined
}

export const ContactForm: React.FC<ContactFormProps> = ({
	contactSubjects,
	address,
	email,
	phone,
}) => {
	const [selectedValue, setSelectedValue] = useState('')

	const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
		setSelectedValue(e.target?.value)
	}

	const {
		register,
		handleSubmit,
		formState: { errors, isSubmitSuccessful, isSubmitting },
		reset,
	} = useForm<Inputs>({
		resolver: yupResolver(contactValidationSchema),
	})
	const onSubmit: SubmitHandler<Inputs> = (data) => {
		sendEmail(data)
	}

	useEffect(() => {
		if (!isSubmitSuccessful) return
		setTimeout(() => {
			reset()
			setSelectedValue('')
		}, 1000)
	}, [isSubmitSuccessful, reset])

	const {
		wrapper,
		base,
		formContainer,
		form,
		inputContainer,
		inputContainerLong,
		input,
		textarea,
		honeypot,
		inputLabel,
		infosContainer,
		selectInput,
		svgArrow,
	} = contactClasses()

	const components = {
		block: {
			normal: ({ children }: any) => (
				<AppText size="base" weight="medium">
					{children}
				</AppText>
			),
		},
		marks: {
			highlight: ({ children }: any) => <span className="text-mainPink">{children}</span>,
		},
	} as PortableTextComponents

	return (
		<div className={wrapper()}>
			<div className={base()}>
				<div className={formContainer()}>
					<form className={form()} onSubmit={handleSubmit(onSubmit)}>
						<div className={inputContainer()}>
							<AppText
								className={inputLabel()}
								as="label"
								size="small"
								weight="medium"
								htmlFor="mainFirstName"
							>
								First Name*
							</AppText>
							<input
								type="text"
								id="mainFirstName"
								className={clsx(
									input(),
									errors.mainFirstName?.message ? 'border border-black' : 'border-transparent'
								)}
								placeholder="First Name"
								{...register('mainFirstName')}
							/>
							{errors.mainFirstName?.message && (
								<AppText size="small" className="absolute -bottom-2">
									{errors.mainFirstName.message}
								</AppText>
							)}
						</div>
						<div className={inputContainer()}>
							<AppText
								className={inputLabel()}
								as="label"
								size="small"
								weight="medium"
								htmlFor="mainLastName"
							>
								Last Name*
							</AppText>
							<input
								type="text"
								id="mainLastName"
								className={clsx(
									input(),
									errors.mainLastName?.message ? 'border-black' : 'border-transparent'
								)}
								placeholder="Last Name"
								{...register('mainLastName')}
							/>
							{errors.mainLastName?.message && (
								<AppText size="small" className="absolute -bottom-2">
									{errors.mainLastName.message}
								</AppText>
							)}
						</div>
						<div className={inputContainerLong()}>
							<AppText
								className={inputLabel()}
								as="label"
								size="small"
								weight="medium"
								htmlFor="mainCompany"
							>
								Company Name*
							</AppText>
							<input
								type="text"
								id="mainCompany"
								className={clsx(
									input(),
									errors.mainCompany?.message ? 'border-black' : 'border-transparent'
								)}
								placeholder="Company Name"
								{...register('mainCompany')}
							/>
							{errors.mainCompany?.message && (
								<AppText size="small" className="absolute -bottom-2">
									{errors.mainCompany.message}
								</AppText>
							)}
						</div>
						<div className={inputContainer()}>
							<AppText
								className={inputLabel()}
								as="label"
								size="small"
								weight="medium"
								htmlFor="mainEmail"
							>
								E-mail*
							</AppText>
							<input
								type="email"
								id="mainEmail"
								className={clsx(
									input(),
									errors.mainEmail?.message ? 'border-black' : 'border-transparent'
								)}
								placeholder="E-mail"
								{...register('mainEmail')}
							/>
							{errors.mainEmail?.message && (
								<AppText size="small" className="absolute -bottom-2">
									{errors.mainEmail.message}
								</AppText>
							)}
						</div>
						<div className={inputContainer()}>
							<AppText
								className={inputLabel()}
								as="label"
								size="small"
								weight="medium"
								htmlFor="mainPhone"
							>
								Phone
							</AppText>
							<input
								type="tel"
								id="mainPhone"
								className={clsx(
									input(),
									errors.mainPhone?.message ? 'border-black' : 'border-transparent'
								)}
								placeholder="Phone"
								{...register('mainPhone')}
							/>
							{errors.mainPhone?.message && (
								<AppText size="small" className="absolute -bottom-2">
									{errors.mainPhone.message}
								</AppText>
							)}
						</div>
						<div className={inputContainerLong()}>
							<AppText
								className={inputLabel()}
								as="label"
								size="small"
								weight="medium"
								htmlFor="mainSubject"
							>
								Subject*
							</AppText>
							<select
								id="mainSubject"
								value={selectedValue}
								className={clsx(
									selectInput({ select: true }),
									errors.mainSubject?.message ? 'border-black' : 'border-transparent',
									'invalid:text-[#9C9C9C]',
									selectedValue !== '' ? 'bg-white/30' : 'bg-white'
								)}
								{...register('mainSubject', {
									required: true,
									onChange: (e) => {
										handleChange(e)
									},
								})}
							>
								<option value="" disabled>
									Select a subject
								</option>
								{contactSubjects?.map((subject, idx) => (
									<option key={`subject-${idx}`} value={subject}>
										{subject}
									</option>
								))}
							</select>
							<SelectArrow className={svgArrow({ select: selectedValue !== '' })} />
							{errors.mainSubject?.message && (
								<AppText size="small" className="absolute -bottom-2">
									{errors.mainSubject.message}
								</AppText>
							)}
						</div>
						<div className={inputContainerLong()}>
							<AppText
								className={inputLabel()}
								as="label"
								size="small"
								weight="medium"
								htmlFor="mainMessage"
							>
								Message*
							</AppText>
							<textarea
								className={clsx(
									textarea(),
									errors.mainMessage?.message ? 'border-black' : 'border-transparent'
								)}
								id="mainMessage"
								placeholder="Your message"
								{...register('mainMessage')}
								rows={6}
							/>
							{errors.mainMessage?.message && (
								<AppText size="small" className="absolute -bottom-2">
									{errors.mainMessage.message}
								</AppText>
							)}
						</div>

						{/* Honeypot */}
						<input
							type="text"
							id="placeOfBirth"
							autoComplete="off"
							className={honeypot()}
							placeholder="Place of Birth"
							{...register('placeOfBirth')}
						/>
						{/* Honeypot */}

						<Button className="col-span-2 w-fit mt-1 md:mt-2" variant="secondary" type="submit">
							{isSubmitSuccessful
								? 'Email has been sent'
								: isSubmitting
									? 'Submitting...'
									: 'Send your message'}
						</Button>
					</form>
				</div>
				<div className={infosContainer()}>
					<AppText as="h2" family="condensed" textTransform="uppercase" size="h3">
						Information
					</AppText>
					<div className="flex flex-col gap-y-2 mt-2 ">
						<p className="flex flex-col">
							<AppLink textProps={{ size: 'base', weight: 'medium' }} href={`mailto:${email}`}>
								{email}
							</AppLink>
							<AppLink textProps={{ size: 'base', weight: 'medium' }} href={`tel:${phone}`}>
								{phone}
							</AppLink>
						</p>
						<p className="flex flex-col">
							<PortableText value={address as PortableTextObject} components={components} />
						</p>
					</div>
				</div>
			</div>
		</div>
	)
}
